import React from 'react';
import {
    Box, Button, Chip, Divider, List, ListItem, ListItemText,
    Popover, Typography,
} from '@mui/material';
import moment from 'moment';

import { ContextStore } from '../store/contextStore';
import { BackgroundTaskContext } from './BackgroundTaskProvider';
import TaskProgressBar from './TaskProgressBar';

const STATE_COLORS = {
    0: 'default',     // pending
    1: 'primary',     // running
    2: 'success',     // completed
    3: 'error',       // failed
    4: 'warning',     // cancelled
    5: 'info',        // awaiting approval
};

const TaskListItem = ({ task, showLicensee, onClick }) => (
    <ListItem
        button
        onClick={() => onClick && onClick(task.id)}
        sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                {task.task_label || task.task_type}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                {moment(task.created_ts).fromNow()}
            </Typography>
        </Box>
        {showLicensee && task.tour_operator?.name && (
            <Typography variant="caption" color="textSecondary" noWrap sx={{ width: '100%' }}>
                {task.tour_operator.name}
            </Typography>
        )}
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
            <Chip
                label={task.state_display}
                size="small"
                color={STATE_COLORS[task.state] || 'default'}
                sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {task.state === 1 && task.progress && (
                <Box sx={{ flexGrow: 1 }}>
                    <TaskProgressBar progress={task.progress} />
                </Box>
            )}
        </Box>
    </ListItem>
);

const TaskPopoverWidget = ({ anchorEl }) => {
    const {
        popoverOpen, setPopoverOpen,
        recentTasks, fetchRecentTasks, taskCache,
        setDialogOpen,
        openDialogForTask,
    } = React.useContext(BackgroundTaskContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);
    const showLicensee = isFranchise && currentLicensee == null;

    // Fetched on open and then every 10s while open, like the full-screen
    // dialog. A single fetch left the list frozen at whatever it showed when
    // opened — a task that finished while the popover was up stayed
    // "Running" until it was closed and reopened (CP-7770).
    React.useEffect(() => {
        if (!popoverOpen) return undefined;
        fetchRecentTasks();
        const interval = setInterval(fetchRecentTasks, 10000);
        return () => clearInterval(interval);
    }, [popoverOpen, fetchRecentTasks]);

    // Between fetches, the provider's own status poll already knows about
    // state changes (it is what drives the completion toasts) — show them.
    // Whichever copy the server stamped later wins; the two polls are not in
    // step, so either one can be the fresher.
    const tasks = React.useMemo(
        () => recentTasks.map((task) => {
            const cached = taskCache[task.id];
            if (!cached) return task;
            const newer = (cached.updated_ts || '') > (task.updated_ts || '');
            return newer ? { ...task, ...cached } : task;
        }),
        [recentTasks, taskCache],
    );

    return (
        <Popover
            open={popoverOpen && Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setPopoverOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { width: 400, maxHeight: 500 } }}
            sx={{ zIndex: 10002 }}
        >
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">Background Operations</Typography>
                <Button
                    size="small"
                    onClick={() => {
                        setPopoverOpen(false);
                        setDialogOpen(true);
                    }}
                >
                    View All
                </Button>
            </Box>
            <Divider />
            <List dense sx={{ maxHeight: 380, overflow: 'auto' }}>
                {tasks.map((task) => (
                    <TaskListItem key={task.id} task={task} showLicensee={showLicensee} onClick={openDialogForTask} />
                ))}
                {tasks.length === 0 && (
                    <ListItem>
                        <ListItemText
                            secondary="No recent operations"
                            secondaryTypographyProps={{ align: 'center' }}
                        />
                    </ListItem>
                )}
            </List>
        </Popover>
    );
};

export default TaskPopoverWidget;
